import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import HBLabs from "../../assets/img/HBL_logo.webp";
import {
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemButton,
  Box,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";

import DandDAPI from "@utilities/smart_api";
import { RoutePath } from "@utilities/smart_api_class";
import { makeStyles } from "@utilities/theme_utilities";
import LoadingBox from "@components/input/LoadingBox";

export const MENU_ROUTES: { [grp in "user" | "admin" | "sourceBooks" | "gameRules" | "info"]: { path: string, name: string, priority?: boolean }[] } = {
  user: [
    {
      path: "/campaign",
      name: "My Campaigns",
    },
    {
      path: "/character",
      name: "My Characters",
    },
  ],
  admin: [
    {
      path: "/all_characters",
      name: "All Characters",
    },
    { 
      path: `/admin_only`,
      name: "Admin Only"
    },
    {
      path: "/floating_feature",
      name: "Features",
    },
    {
      path: "/creature_feature",
      name: "Creature Features",
    },
    {
      path: "/admin/feature_usage",
      name: "Feature Usage",
    },
    {
      path: "/admin/spell_list_repair",
      name: "Repair Spell Lists",
    },
    {
      path: "/admin/table_field_repair",
      name: "Table Field Repair",
    },
    {
      path: "/ticket",
      name: "Tickets",
    },
    {
      path: "/user_ticket",
      name: "User Tickets",
    },
    {
      path: "/creature_group",
      name: "Creature Groups",
    },
    {
      path: "/reusable_string",
      name: "Reusable Strings",
    },
    {
      path: "/error_log",
      name: "Error Logs",
    },
  ],
  sourceBooks: [
    {
      path: "/source_book",
      name: "Source Books",
    },
  ],
  gameRules: [
    // {
    //   path: "/search",
    //   name: "Search",
    //   priority: true
    // },
    {
      path: "/background",
      name: "Backgrounds",
    },
    {
      path: "/bastion_facility",
      name: "Bastion Facilities",
    },
    {
      path: "/game_card",
      name: "Cards",
    },
    {
      path: "/game_class",
      name: "Classes",
    },
    {
      path: "/condition",
      name: "Conditions",
    },
    {
      path: "/creature",
      name: "Creatures",
    },
    {
      path: "/creature_gift",
      name: "Creature Gifts",
    },
    {
      path: "/game_deck",
      name: "Decks",
    },
    {
      path: "/deity",
      name: "Deities",
    },
    {
      path: "/eldritch_invocation",
      name: "Eldritch Invocations",
    },
    {
      path: "/encounter_complication",
      name: "Encounter Complications",
    },
    {
      path: "/environment",
      name: "Environments",
    },
    {
      path: "/equipment_pack",
      name: "Equipment Packs",
    },
    {
      path: "/feat",
      name: "Feats",
    },
    {
      path: "/fighting_style",
      name: "Fighting Styles",
    },
    {
      path: "/gift",
      name: "Gifts",
    },
    {
      path: "/item",
      name: "Items",
    },
    {
      path: "/magic_item_keyword",
      name: "Magic Item Keywords",
    },
    {
      path: "/pact_boon",
      name: "Pact Boons",
    },
    {
      path: "/race",
      name: "Races/Species",
    },
    {
      path: "/rollable_table",
      name: "Rollable Tables",
    },
    {
      path: "/rule_definition",
      name: "Rules Definitions",
    },
    {
      path: "/special_feature",
      name: "Special Features",
    },
    {
      path: "/spell",
      name: "Spells",
    },
    {
      path: "/spell_list",
      name: "Spell Lists",
    },
    {
      path: "/spell_slot_type",
      name: "Spell Slot Types",
    },
    {
      path: "/standard_features",
      name: "Standard Features",
    },
    {
      path: "/subclass",
      name: "Subclasses",
    },
    {
      path: "/subrace",
      name: "Subraces/Lineages",
    },
    {
      path: "/table",
      name: "Tables",
    },
    {
      path: "/tool",
      name: "Tools",
    },
    {
      path: "/weapon_mastery_property",
      name: "Weapon Mastery Properties"
    },
    {
      path: "/loot_table",
      name: "Loot Tables",
    },
  ],
  info: [
    {
      path: "/attributions",
      name: "Attributions",
    },
    {
      path: "/ogl",
      name: "Open Game License",
    },
    {
      path: "/privacy",
      name: "Privacy Policy",
    },
    {
      path: "/data-deletion",
      name: "Data Deletion",
    },
  ],
};

interface Props {
  logoText: string;
  onNavigate: () => void;
}

const api = DandDAPI.getInstance();

export default function Sidebar({ logoText, onNavigate }: Props) {
  const [reloading, setReloading] = useState(false);
  const [openSections, setOpenSections] = useState<{[key: string]: boolean}>({
    user: true,
    admin: false,
    sourceBooks: false,
    gameRules: false,
    info: false,
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const useStyles = makeStyles()((theme) => {
    return ({
      sidebarList: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      },
      submenuHeader: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      },
      linkButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      },
      submenuContainer: {
        border: '1px solid',
        borderTop: 'none',
        marginBottom: 0,
        paddingLeft: theme.spacing(1),
        overflow: 'hidden',
      },
      nestedItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: '1px solid',
        marginBottom: 0,
        minWidth: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          opacity: 0.9,
        },
        '& .MuiListItemText-primary': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      }
    });
  });
  const { classes } = useStyles();

  const handleToggle = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const link = (prop: RoutePath, key: number, nested = false) => {
    return (
      <ListItemButton 
        key={key}
        component={NavLink}
        to={prop.path}
        className={nested ? classes.nestedItem : classes.linkButton}
        onClick={onNavigate}
      >
        <ListItemText primary={prop.name} />
      </ListItemButton>
    );
  }

  const submenuHeader = (title: string, section: string) => {
    return (
      <ListItemButton 
        className={classes.submenuHeader}
        onClick={() => handleToggle(section)}
      >
        <ListItemText primary={title} />
        {openSections[section] ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
    );
  };

  const links = (group: keyof typeof MENU_ROUTES, nested = false) => {
    return (
      MENU_ROUTES[group]
        .sort((a, b) => {
          if (a.priority === b.priority || // Same Priority, so alphabetize.
            (!a.priority && !b.priority)) { // One is undefined and the other is false. Treat as same.
            return a.name.localeCompare(b.name);
          } else { // One is true and the other is not.
            if (a.priority) {
              return -1;
            } else {
              return 1;
            }
          }
        })
        .map((prop, key) => link(prop, key, nested))
    );
  }
  
  const brand = () => {
    return (
      <NavLink to={`/`} className="WhiteTextButton" onClick={onNavigate}>
        <ListItem style={{ height: "55px" }}>
          <img src={HBLabs} alt="logo" className="HBLabs-logo" />
          <ListItemText primary={logoText} />
        </ListItem>
      </NavLink>
    );
  }

  if (reloading) {
    return <LoadingBox />;
  } else {
    return (
      <List className={classes.sidebarList}>
        {brand()}
        
        {/* User Section */}
        {api.user && (
          <>
            {submenuHeader("My Content", "user")}
            <Collapse in={openSections.user} timeout="auto" unmountOnExit>
              <Box className={classes.submenuContainer}>
                <List component="div" disablePadding>
                  {links("user", true)}
                </List>
              </Box>
            </Collapse>
          </>
        )}

        {/* Admin Section */}
        {api.user && api.user.admin && (
          <>
            {submenuHeader("Admin", "admin")}
            <Collapse in={openSections.admin} timeout="auto" unmountOnExit>
              <Box className={classes.submenuContainer}>
                <List component="div" disablePadding>
                  {links("admin", true)}
                </List>
              </Box>
            </Collapse>
          </>
        )}

        {/* Source Books - Direct Links */}
        {links("sourceBooks")}

        {/* Game Rules Section */}
        {submenuHeader("Game Rules", "gameRules")}
        <Collapse in={openSections.gameRules} timeout="auto" unmountOnExit>
          <Box className={classes.submenuContainer}>
            <List component="div" disablePadding>
              {links("gameRules", true)}
            </List>
          </Box>
        </Collapse>

        {/* Info Section */}
        {submenuHeader("Information", "info")}
        <Collapse in={openSections.info} timeout="auto" unmountOnExit>
          <Box className={classes.submenuContainer}>
            <List component="div" disablePadding>
              {links("info", true)}
            </List>
          </Box>
        </Collapse>
        <p className="padded">v.{ api.version }</p>
      </List>
    );
  }
}